import { format } from 'date-fns'
import { toString } from 'lodash'
import currency from 'currency.js'

const getSchemaURI = (name, version) => `iglu:com.foodsby/${name}/jsonschema/${version}`

/**************
 * Global
 *************/
export const addGlobalEntities = async entities => {
  // Replace the context if exists
  // TODO we might want to figure out a way to avoid replacing if the data did not change
  if (window.snowplow) {
    await window.snowplow('removeGlobalContexts', entities)
    await window.snowplow('addGlobalContexts', entities)
  }
}
export const pushSnowplow = (schema, localEntities) =>
  window.snowplow && window.snowplow('trackSelfDescribingEvent', schema, localEntities)

const SCHEMA_NAME = Object.freeze({
  DELIVERY: 'delivery',
  LOCATION: 'location',
  ORDER: 'order',
  USER: 'user',
  TEAM: 'team',
  ORDER_PROCESS: 'order_process',
  SIGN_UP_PROCESS: 'sign_up_process',
  USER_ONBOARDING_PROCESS: 'user_onboarding_process',
  TEAMS_FEATURE: 'teams_feature',
})

/**************
 * To Schemas
 *************/
export const toSignupProcessSchema = (
  stepType,
  stepName,
  stepValue = undefined,
  failureReason = undefined,
) => ({
  data: {
    step_name: stepName,
    step_type: stepType,
    step_value: stepValue,
    failure_reason: failureReason && toString(failureReason),
  },
  schema: getSchemaURI(SCHEMA_NAME.SIGN_UP_PROCESS, '1-0-1'),
})

export const toOrderProcessSchema = (
  stepType,
  stepName,
  stepValue = undefined,
  failureReason = undefined,
) => ({
  data: {
    step_name: stepName,
    step_type: stepType,
    step_value: stepValue,
    failure_reason: failureReason && toString(failureReason),
  },
  schema: getSchemaURI(SCHEMA_NAME.ORDER_PROCESS, '1-0-1'),
})

export const toUserUnboardingProcessSchema = (action, step, value, failureReason) => ({
  data: {
    action,
    step,
    value,
    failure_reason: failureReason && toString(failureReason),
  },
  schema: getSchemaURI(SCHEMA_NAME.USER_ONBOARDING_PROCESS, '1-0-0'),
})

export const toTeamsFeatureSchema = (
  view,
  viewType,
  actionType,
  actionTarget,
  actionTargetType,
  actionValue = undefined,
  failureReason = undefined,
) => ({
  data: {
    view,
    view_type: viewType,
    action_type: actionType,
    action_target: actionTarget,
    action_target_type: actionTargetType,
    action_value: actionValue,
    failure_reason: failureReason && toString(failureReason),
  },
  schema: getSchemaURI(SCHEMA_NAME.TEAMS_FEATURE, '1-0-1'),
})

export const toUserSchema = (id, username) => ({
  data: {
    id,
    username,
  },
  schema: getSchemaURI(SCHEMA_NAME.USER, '1-0-0'),
})

export const toLocationSchema = id => ({
  data: {
    id,
  },
  schema: getSchemaURI(SCHEMA_NAME.LOCATION, '1-0-0'),
})

export const toOrderSchema = (
  id,
  totalInCents,
  isLate,
  isDelivered,
  isCanceled,
  programId,
  couponId,
  giftId,
) => ({
  data: {
    coupon_id: couponId,
    gift_id: giftId,
    id,
    is_canceled: isCanceled,
    is_delivered: isDelivered,
    is_late: isLate,
    program_id: programId,
    total_in_cents: totalInCents,
  },
  schema: getSchemaURI(SCHEMA_NAME.ORDER, '1-0-0'),
})

export const toDeliverySchema = (
  deliveryDropoffId,
  verticalListPos,
  horizontalListPos,
  status,
  storeId,
  cutoffTime,
  dropoffTime,
  menuId,
  date,
) => ({
  data: {
    cutoff_time: cutoffTime,
    date,
    delivery_dropoff_id: deliveryDropoffId,
    dropoff_time: dropoffTime,
    horizontal_list_pos: horizontalListPos,
    menu_id: menuId,
    status,
    store_id: storeId,
    vertical_list_pos: verticalListPos,
  },
  schema: getSchemaURI(SCHEMA_NAME.DELIVERY, '1-0-0'),
})

export const toTeamSchema = accountId => ({
  data: {
    id: accountId,
  },
  schema: getSchemaURI(SCHEMA_NAME.TEAM, '1-0-0'),
})

/**************
 * To Entities
 *************/
export const toDeliveryEntities = deliveries =>
  deliveries?.map(delivery => {
    const date = format(delivery.date, 'YYYY-MM-DD')
    return toDeliverySchema(
      delivery.deliveryDropoffId,
      undefined, // TODO Add these vertical/horizontal positions of a delivery card
      undefined,
      delivery.isPastCutoff ? 'isPastCutoff' : 'isBeforeCutoff',
      delivery.storeId,
      date + 'T' + delivery.cutoff,
      date + 'T' + delivery.dropoff,
      delivery.menuId,
      date,
    )
  })

export const toOrderEntities = orders =>
  orders?.map(order => {
    const orderTotalInCents = currency(order.orderTotal).intValue
    return toOrderSchema(
      order.orderId,
      orderTotalInCents,
      order.orderState === 'LATE',
      order.isDelivered,
      order.orderState === 'CANCELED',
    )
  })
